import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Target, CheckCircle2, Circle, XCircle, Eye, EyeOff, Edit3, Folder, FolderOpen, ChevronDown, ChevronRight, Move } from "lucide-react";
import { useConfirm } from "@/components/ConfirmDialog";
import { RelatedEntitiesPanel } from "@/components/RelatedEntitiesPanel";
import EntityContextMenu from "@/components/EntityContextMenu";
import { RichTextEditor, RichTextDisplay } from "@/components/RichTextEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  { key: "active",    label: "Active",    color: "text-primary",    bg: "bg-primary/10",    border: "border-primary/40" },
  { key: "completed", label: "Completed", color: "text-green-400", bg: "bg-green-500/10",   border: "border-green-500/40" },
  { key: "failed",    label: "Failed",    color: "text-red-400",   bg: "bg-red-500/10",     border: "border-red-500/40" },
];

export const QuestsPage = () => {
  const { campaignId } = useParams();
  const confirm = useConfirm();
  const [quests, setQuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null); // null | "new" | quest
  const [draft, setDraft] = useState({ title: "", description: "", dm_notes: "", reward: "", folder: "", status: "active", visibility: "all" });
  const [newObjective, setNewObjective] = useState({});
  const [collapsedFolders, setCollapsedFolders] = useState(() => new Set());
  const [movingId, setMovingId] = useState(null); // quest.id currently being moved to folder

  const fetchQuests = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/campaigns/${campaignId}/quests`, { withCredentials: true });
      setQuests(res.data || []);
    } catch { toast.error("Failed to load quests"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchQuests(); }, [campaignId]);

  const openNew = () => {
    setDraft({ title: "", description: "", dm_notes: "", reward: "", folder: "", status: "active", visibility: "all" });
    setEditing("new");
  };
  const openEdit = (q) => {
    setDraft({ title: q.title, description: q.description || "", dm_notes: q.dm_notes || "", reward: q.reward || "", folder: q.folder || "", status: q.status, visibility: q.visibility || "all" });
    setEditing(q);
  };

  const moveToFolder = async (q, folder) => {
    setMovingId(q.id);
    try {
      const res = await axios.put(`${API}/quests/${q.id}`, { folder }, { withCredentials: true });
      setQuests((p) => p.map((x) => (x.id === q.id ? res.data : x)));
      toast.success(folder ? `Moved to "${folder}"` : "Removed from folder");
    } catch { toast.error("Move failed"); }
    finally { setMovingId(null); }
  };

  const toggleFolderCollapsed = (name) => {
    setCollapsedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const saveQuest = async () => {
    if (!draft.title.trim()) { toast.error("Title required"); return; }
    try {
      if (editing === "new") {
        const res = await axios.post(`${API}/quests`, { campaign_id: campaignId, ...draft }, { withCredentials: true });
        setQuests(p => [res.data, ...p]);
        toast.success("Quest created");
      } else {
        const res = await axios.put(`${API}/quests/${editing.id}`, draft, { withCredentials: true });
        setQuests(p => p.map(q => q.id === editing.id ? res.data : q));
        toast.success("Quest updated");
      }
      setEditing(null);
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };

  const removeQuest = async (q) => {
    if (!await confirm({ title: "Delete quest?", description: `"${q.title}" will be removed.` })) return;
    try {
      await axios.delete(`${API}/quests/${q.id}`, { withCredentials: true });
      setQuests(p => p.filter(x => x.id !== q.id));
      toast.success("Quest deleted");
    } catch { toast.error("Delete failed"); }
  };

  const cycleStatus = async (q) => {
    const next = q.status === "active" ? "completed" : q.status === "completed" ? "failed" : "active";
    try {
      const res = await axios.put(`${API}/quests/${q.id}`, { status: next }, { withCredentials: true });
      setQuests(p => p.map(x => x.id === q.id ? res.data : x));
    } catch { toast.error("Update failed"); }
  };

  const toggleObjective = async (q, obj) => {
    try {
      await axios.patch(`${API}/quests/${q.id}/objectives/${obj.id}`, { completed: !obj.completed }, { withCredentials: true });
      setQuests(p => p.map(x => x.id === q.id ? { ...x, objectives: x.objectives.map(o => o.id === obj.id ? { ...o, completed: !obj.completed } : o) } : x));
    } catch { toast.error("Failed to update"); }
  };

  const addObjective = async (q) => {
    const text = (newObjective[q.id] || "").trim();
    if (!text) return;
    try {
      const res = await axios.post(`${API}/quests/${q.id}/objectives`, { text }, { withCredentials: true });
      setQuests(p => p.map(x => x.id === q.id ? { ...x, objectives: [...(x.objectives || []), res.data] } : x));
      setNewObjective(p => ({ ...p, [q.id]: "" }));
    } catch { toast.error("Failed to add objective"); }
  };

  const deleteObjective = async (q, oid) => {
    try {
      await axios.delete(`${API}/quests/${q.id}/objectives/${oid}`, { withCredentials: true });
      setQuests(p => p.map(x => x.id === q.id ? { ...x, objectives: x.objectives.filter(o => o.id !== oid) } : x));
    } catch { toast.error("Failed to remove"); }
  };

  const filtered = filter === "all" ? quests : quests.filter(q => q.status === filter);
  const counts = STATUSES.reduce((acc, s) => ({ ...acc, [s.key]: quests.filter(q => q.status === s.key).length }), {});

  return (
    <Layout>
      <div className="w-full" data-testid="quests-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-serif text-foreground flex items-center gap-2"><Target className="w-7 h-7 text-primary" /> Quests</h1>
            <p className="text-sm text-muted-foreground mt-1">Track active jobs, completed deeds, and the broken promises along the way.</p>
          </div>
          <Button onClick={openNew} className="bg-primary text-primary-foreground rounded-sm" data-testid="new-quest-button">
            <Plus className="w-4 h-4 mr-1" /> New Quest
          </Button>
        </div>

        {/* Filter pills */}
        <div className="flex gap-2 mb-4 flex-wrap" data-testid="quest-filters">
          <button
            onClick={() => setFilter("all")}
            className={`text-xs px-3 py-1 rounded-sm border ${filter === "all" ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}
            data-testid="filter-all"
          >All ({quests.length})</button>
          {STATUSES.map(s => (
            <button
              key={s.key}
              onClick={() => setFilter(s.key)}
              className={`text-xs px-3 py-1 rounded-sm border ${filter === s.key ? `${s.bg} ${s.color} ${s.border}` : "border-border text-muted-foreground hover:text-foreground"}`}
              data-testid={`filter-${s.key}`}
            >{s.label} ({counts[s.key] || 0})</button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <p className="text-muted-foreground text-sm">Loading…</p>
        ) : filtered.length === 0 ? (
          <Card className="bg-card border-border" data-testid="quests-empty">
            <CardContent className="p-8 text-center">
              <Target className="w-10 h-10 text-muted-foreground/50 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">{quests.length === 0 ? "No quests yet. Click New Quest to add the first job." : `No ${filter} quests.`}</p>
            </CardContent>
          </Card>
        ) : (() => {
          // Group by folder — empty string collects into "Unfiled". Sort folders alphabetically, unfiled last.
          const groups = new Map();
          for (const q of filtered) {
            const key = (q.folder || "").trim();
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push(q);
          }
          const folderNames = [...groups.keys()].sort((a, b) => {
            if (a === "") return 1;
            if (b === "") return -1;
            return a.localeCompare(b);
          });
          // List of all folder names (including empty tracked in quest state) — used for the Move-to popover
          const allFolderNames = [...new Set(quests.map((q) => (q.folder || "").trim()).filter(Boolean))].sort();
          return (
            <div className="space-y-6" data-testid="quests-grid">
              {folderNames.map((folder) => {
                const items = groups.get(folder);
                const isCollapsed = collapsedFolders.has(folder);
                const label = folder || "Unfiled";
                return (
                  <div key={label} data-testid={`quest-folder-${label}`}>
                    <button
                      onClick={() => toggleFolderCollapsed(folder)}
                      className="w-full flex items-center gap-2 mb-3 group text-left"
                      data-testid={`folder-toggle-${label}`}
                    >
                      {isCollapsed ? <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground group-hover:text-foreground" />}
                      {folder ? <FolderOpen className="w-4 h-4 text-primary" /> : <Folder className="w-4 h-4 text-muted-foreground" />}
                      <h2 className="text-sm font-serif text-foreground uppercase tracking-wider">{label}</h2>
                      <span className="text-xs text-muted-foreground">({items.length})</span>
                      <div className="flex-1 border-b border-border/40 mx-2" />
                    </button>
                    {!isCollapsed && (
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                        {items.map(q => {
                          const status = STATUSES.find(s => s.key === q.status) || STATUSES[0];
                          const totalObjs = (q.objectives || []).length;
                          const doneObjs = (q.objectives || []).filter(o => o.completed).length;
                          return (
                            <EntityContextMenu
                              key={q.id}
                              onRename={() => openEdit(q)}
                              onDelete={() => removeQuest(q)}
                              pinPath="quests"
                              pinLabel={`Quest: ${q.title}`}
                              entityName={q.title}
                            >
                              <Card className={`bg-card border-l-4 ${status.border} border-y border-r border-border`} data-testid={`quest-card-${q.id}`}>
                                <CardContent className="p-3">
                                  <div className="flex items-start gap-2 mb-1.5">
                                    <button
                                      onClick={() => cycleStatus(q)}
                                      className={`flex-shrink-0 mt-0.5 ${status.color}`}
                                      title={`Status: ${status.label} (click to cycle)`}
                                      data-testid={`cycle-status-${q.id}`}
                                    >
                                      {q.status === "completed" ? <CheckCircle2 className="w-4 h-4" /> : q.status === "failed" ? <XCircle className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
                                    </button>
                                    <h3 className={`flex-1 font-serif text-sm leading-tight ${q.status === "completed" ? "line-through text-muted-foreground" : "text-foreground"}`}>{q.title}</h3>
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                      <Popover>
                                        <PopoverTrigger asChild>
                                          <button className="text-muted-foreground hover:text-primary" title="Move to folder" data-testid={`move-quest-${q.id}`}>
                                            <Move className="w-3.5 h-3.5" />
                                          </button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-56 p-2 bg-popover border-border" align="end">
                                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5 px-1">Move to folder</p>
                                          <div className="space-y-0.5 max-h-64 overflow-y-auto">
                                            <button
                                              onClick={() => moveToFolder(q, "")}
                                              disabled={movingId === q.id}
                                              className={`w-full text-left text-xs px-2 py-1 rounded hover:bg-accent flex items-center gap-1.5 ${!q.folder ? "text-primary" : "text-foreground"}`}
                                              data-testid={`move-target-unfiled-${q.id}`}
                                            >
                                              <Folder className="w-3 h-3" /> Unfiled
                                            </button>
                                            {allFolderNames.filter(f => f !== q.folder).map(f => (
                                              <button
                                                key={f}
                                                onClick={() => moveToFolder(q, f)}
                                                disabled={movingId === q.id}
                                                className="w-full text-left text-xs px-2 py-1 rounded hover:bg-accent flex items-center gap-1.5 text-foreground"
                                                data-testid={`move-target-${f}-${q.id}`}
                                              >
                                                <FolderOpen className="w-3 h-3 text-primary" /> {f}
                                              </button>
                                            ))}
                                          </div>
                                          <div className="border-t border-border mt-2 pt-2">
                                            <Input
                                              placeholder="New folder…"
                                              className="h-7 text-xs"
                                              onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                  const v = e.currentTarget.value.trim();
                                                  if (v) { moveToFolder(q, v); e.currentTarget.value = ""; }
                                                }
                                              }}
                                              data-testid={`new-folder-input-${q.id}`}
                                            />
                                          </div>
                                        </PopoverContent>
                                      </Popover>
                                      <button onClick={() => openEdit(q)} className="text-muted-foreground hover:text-foreground" data-testid={`edit-quest-${q.id}`}><Edit3 className="w-3.5 h-3.5" /></button>
                                      <button onClick={() => removeQuest(q)} className="text-muted-foreground hover:text-red-400" data-testid={`delete-quest-${q.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                                    <span className={`text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-sm ${status.bg} ${status.color} ${status.border} border`}>{status.label}</span>
                                    {q.visibility === "dm_only" && (
                                      <span className="text-[9px] uppercase text-amber-400 flex items-center gap-0.5"><EyeOff className="w-2.5 h-2.5" />DM only</span>
                                    )}
                                    {totalObjs > 0 && (
                                      <span className="text-[9px] text-muted-foreground ml-auto">{doneObjs}/{totalObjs} obj</span>
                                    )}
                                  </div>
                                  {q.description && (
                                    <div className="text-xs text-foreground/70 mb-2 leading-snug line-clamp-3">
                                      <RichTextDisplay content={q.description} />
                                    </div>
                                  )}
                                  {q.reward && (
                                    <p className="text-[11px] text-yellow-300/90 mb-1.5 line-clamp-1">
                                      <span className="text-muted-foreground">Reward:</span> {q.reward}
                                    </p>
                                  )}
                                  {q.dm_notes && (
                                    <details className="mb-1.5" data-testid={`quest-dm-notes-${q.id}`}>
                                      <summary className="cursor-pointer text-[10px] uppercase tracking-wider text-amber-400/80 flex items-center gap-1 hover:text-amber-300 select-none">
                                        <EyeOff className="w-3 h-3" /> DM Notes
                                      </summary>
                                      <div className="mt-1 text-xs text-amber-50/85 p-2 bg-amber-500/5 border border-amber-500/20 border-dashed rounded-sm leading-snug max-h-32 overflow-y-auto">
                                        <RichTextDisplay content={q.dm_notes} />
                                      </div>
                                    </details>
                                  )}
                                  {totalObjs > 0 && (
                                    <details className="mb-1.5">
                                      <summary className="cursor-pointer text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground select-none">
                                        Objectives ({doneObjs}/{totalObjs})
                                      </summary>
                                      <div className="space-y-0.5 mt-1">
                                        {(q.objectives || []).map(o => (
                                          <div key={o.id} className="flex items-start gap-1.5 group" data-testid={`objective-${o.id}`}>
                                            <button
                                              onClick={() => toggleObjective(q, o)}
                                              className={`mt-0.5 flex-shrink-0 ${o.completed ? "text-green-400" : "text-muted-foreground hover:text-foreground"}`}
                                              data-testid={`toggle-objective-${o.id}`}
                                            >
                                              {o.completed ? <CheckCircle2 className="w-3 h-3" /> : <Circle className="w-3 h-3" />}
                                            </button>
                                            <span className={`text-xs flex-1 ${o.completed ? "line-through text-muted-foreground" : "text-foreground/85"}`}>{o.text}</span>
                                            <button onClick={() => deleteObjective(q, o.id)} className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 transition-opacity"><Trash2 className="w-3 h-3" /></button>
                                          </div>
                                        ))}
                                      </div>
                                    </details>
                                  )}
                                  <div className="flex gap-1 mt-1">
                                    <Input
                                      value={newObjective[q.id] || ""}
                                      onChange={(e) => setNewObjective(p => ({ ...p, [q.id]: e.target.value }))}
                                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addObjective(q); } }}
                                      placeholder="Add objective…"
                                      className="h-6 text-[11px] bg-input border-border"
                                      data-testid={`new-objective-input-${q.id}`}
                                    />
                                    <Button onClick={() => addObjective(q)} size="sm" variant="outline" className="h-6 px-1.5 rounded-sm border-border" data-testid={`add-objective-${q.id}`}><Plus className="w-3 h-3" /></Button>
                                  </div>
                                </CardContent>
                              </Card>
                            </EntityContextMenu>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* Edit/New dialog */}
        <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent className="bg-card border-border max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="quest-dialog">
            <DialogHeader>
              <DialogTitle className="font-serif">{editing === "new" ? "New Quest" : "Edit Quest"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <Input value={draft.title} onChange={e => setDraft({ ...draft, title: e.target.value })} placeholder="Quest title" className="bg-input border-border text-lg" data-testid="quest-title-input" />
              <div>
                <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5 block flex items-center gap-1.5">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  Player-facing brief — shared when revealed
                </label>
                <div className="rounded-sm border border-border bg-input">
                  <RichTextEditor
                    value={draft.description}
                    onChange={(v) => setDraft({ ...draft, description: v })}
                    placeholder="The hook the players hear — a pale-eyed merchant offers 500gp to recover a relic from the Greylands…"
                    data-testid="quest-description-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-[10px] text-amber-400 uppercase tracking-wider mb-1.5 block flex items-center gap-1.5">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400" />
                  DM-only notes — never shared with players
                </label>
                <div className="rounded-sm border border-amber-500/30 bg-amber-500/5">
                  <RichTextEditor
                    value={draft.dm_notes}
                    onChange={(v) => setDraft({ ...draft, dm_notes: v })}
                    placeholder="The full quest plan — twists, hidden enemies, secret rooms, encounter stat blocks, treasure tables, plot reveals. Full story-section layout. Images, maps, and formatting all welcome."
                    data-testid="quest-dm-notes-input"
                  />
                </div>
              </div>
              <Input value={draft.reward} onChange={e => setDraft({ ...draft, reward: e.target.value })} placeholder="Reward (e.g. 500gp + magic item)" className="bg-input border-border" data-testid="quest-reward-input" />
              <div>
                <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 block flex items-center gap-1.5">
                  <Folder className="w-3 h-3" /> Folder <span className="text-muted-foreground/60 normal-case tracking-normal">(optional)</span>
                </label>
                <Input
                  value={draft.folder}
                  onChange={e => setDraft({ ...draft, folder: e.target.value })}
                  placeholder="e.g. Main Story · Side Quests · Chapter 1"
                  list="quest-folder-suggestions"
                  className="bg-input border-border"
                  data-testid="quest-folder-input"
                />
                <datalist id="quest-folder-suggestions">
                  {[...new Set(quests.map((q) => q.folder).filter(Boolean))].map((f) => (
                    <option key={f} value={f} />
                  ))}
                </datalist>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 block">Status</label>
                  <Select value={draft.status} onValueChange={v => setDraft({ ...draft, status: v })}>
                    <SelectTrigger className="bg-input border-border" data-testid="quest-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUSES.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 block">Visibility</label>
                  <Select value={draft.visibility} onValueChange={v => setDraft({ ...draft, visibility: v })}>
                    <SelectTrigger className="bg-input border-border" data-testid="quest-visibility-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All players</SelectItem>
                      <SelectItem value="dm_only">DM only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {editing && editing !== "new" && editing.id && (
                <div className="pt-3 border-t border-border">
                  <RelatedEntitiesPanel entityType="quests" entityId={editing.id} />
                </div>
              )}
              <div className="flex gap-2 justify-end pt-2">
                <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
                <Button onClick={saveQuest} className="bg-primary text-primary-foreground" data-testid="save-quest">{editing === "new" ? "Create Quest" : "Save Changes"}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default QuestsPage;
